import type { CatalogOperationsTrigger, FlowOption } from '../types/runs.api.types';

/** The review-only flow catalog-service runs for a feed audit: no APPROVAL, no DELIVERY. */
export const AUDIT_FLOW = 'FEED_AUDIT';

/** Products one audit may cover unless `lily_bypass_audit_cap` lifts it — the legacy Feed Audit
 *  rule (`MAX_AUDIT_PRODUCTS`). A UI rule in both stacks: neither backend enforces it. */
export const AUDIT_MAX_PRODUCTS = 20;

/** Whether a run of `flow` must name the connector's required scope filters (or product ids).
 *  An unknown flow, or an older backend without the field, is treated as guarded — fail closed. */
export function flowNeedsScope(flow: FlowOption | undefined): boolean {
  return flow?.scopeFiltersRequired !== false;
}

export type AuditCapProblem = 'tooManyProducts' | 'maxProductsOverCap';

/** Why an audit body breaks the per-audit product cap, or `null` when it fits (or the cap is lifted). */
export function auditCapProblem(
  body: Pick<CatalogOperationsTrigger, 'productIds' | 'maxProducts'>,
  capLifted: boolean,
): AuditCapProblem | null {
  if (capLifted) return null;
  const picked = body.productIds?.length ?? 0;
  if (picked > AUDIT_MAX_PRODUCTS) return 'tooManyProducts';
  if (picked === 0 && (body.maxProducts ?? 0) > AUDIT_MAX_PRODUCTS) return 'maxProductsOverCap';
  return null;
}

/**
 * The audit body as sent: an audit scoped by filters (or by nothing — the whole sync) is capped
 * through `maxProducts`, which extract honours, so it can never enrich more than the cap. An
 * explicit product list is its own scope and is left alone (the server ignores `maxProducts` then).
 */
export function withAuditCap(
  body: CatalogOperationsTrigger,
  capLifted: boolean,
): CatalogOperationsTrigger {
  if (capLifted || (body.productIds?.length ?? 0) > 0) return body;
  return {
    ...body,
    maxProducts: Math.min(body.maxProducts ?? AUDIT_MAX_PRODUCTS, AUDIT_MAX_PRODUCTS),
  };
}
